// Misti's face - the screen inside her BellaBot-style shell, drawn the way
// BellaBot's own screen draws it.
//
// BellaBot's classic eyes - big warm-white eyes with a large dark pupil that
// looks around inside them, and two sparkle highlights - with small arched
// brows, a little "ω" cat mouth and fine whiskers, light on a dark screen, no
// colour in the face. When she talks the "ω" becomes a mouth that takes the
// shape of each sound she makes - open for "aa", wide for "ee", round for "oo",
// nearly shut for "m" and "s" - measured from her actual voice. Every expression
// is made from those few strokes: the eyes squeeze into "^ ^" or wink as a "<",
// the lids droop, the brows lift or knit, the whiskers perk up or droop, the
// whole face tilts and bobs.
// Conversation status shows as a thin glow on the screen edge.
//
// React renders the SVG once and re-renders only when the conversation state or
// emotion changes (a few times a minute), to swap the edge glow. All motion -
// springs, blinks, glances, breathing, the mouth following her voice - runs in
// one requestAnimationFrame loop that writes attributes through refs, so nothing
// per-frame goes through React.

import React, { useEffect, useRef, useSyncExternalStore } from "react";
import { faceStore } from "../engine/faceStore";
import { DEFAULTS, STIFFNESS, targetFor, auraFor } from "./params";
import { stepGlow, auraOpacity } from "./aura";
import "./bella.scss";

// Geometry, in a 1600 x 1000 viewBox.
const EYE = { w: 344, h: 376, y: 470, left: 520, right: 1080 };
// The pupil, at eye scale 1, and how far it can look inside the eye. Tuned so a
// full glance brings it just up to the eye's edge: any further and the white
// vanishes on that side, which reads as the eye being cut off.
const IRIS = { rx: 132, ry: 146, travelX: 34, travelY: 34 };
const LID_LINE = 16; // the warm-white edge of a lowered lid
const BROW = { y: 214, inset: 10 }; // inset nudges the brows towards the centre
const PIVOT = { x: 800, y: 480 }; // the face rotates about this point
const MOUTH = { x: 800, y: 600, hw: 44, dip: 15 };
// Whiskers pivot on their inner ends, low on the cheeks outside the eyes.
const WHISKER = { x: 318, y: 648 };
const ARCH = 64; // how far the upper lid's edge bows upward when open
const DOTS_DX = 56; // spacing of the "looking it up" dots
// A closed mode first squeezes the open eye shut through the same aperture as a
// blink (fully shut by closure 0.8), and its line fades in over 0.5..1 - so a
// line never sits on top of a half-visible eye.
const CLOSE = { shutAt: 0.8, arcFrom: 0.5 };
// How gaze moves things: the pupils look around inside the eyes, the eyes
// themselves travel a little, the eye on the side she looks towards grows a touch
// (perspective), the highlights lag behind the pupil (a reflection of the room,
// which is what makes an eye look glossy), and the brows, mouth and head follow
// part of the way.
const GAZE = { x: 30, y: 24, persp: 0.06, narrow: 0.06, flat: 0.04, shineLag: 0.3, mouthX: 1, mouthY: 0.9, headX: 12, headY: 8 };

// The closed-eye lines, in the eye's own coordinates.
const ARC_UP = "M -136 50 Q 0 -134 136 50"; // "^" - smiling
const ARC_SOFT = "M -134 -10 C -78 70 78 70 134 -10"; // "u" - softly shut, bowing
const ARC_DOWN = "M -128 40 C -60 66 60 66 128 40"; // a low line - asleep
const WINK = "M 84 -84 L -80 0 L 84 84"; // "<" - BellaBot's wink, pointing in
// "> <" - both eyes squeezed tight shut, for crying.
const SQUEEZE_L = "M -74 -68 L 72 0 L -74 68";
const SQUEEZE_R = "M 74 -68 L -72 0 L 74 68";
// A falling tear, drawn around its own middle.
const TEAR = "M 0 -36 C 16 -12 25 4 25 18 C 25 37 -25 37 -25 18 C -25 4 -16 -12 0 -36 Z";
// Frustration's cross mark - the four short curved strokes of a manga "vein".
const CROSS = "M -30 -8 Q -10 -10 -8 -30 M 8 -30 Q 10 -10 30 -8 M 30 8 Q 10 10 8 30 M -8 30 Q -10 10 -30 8";

const fx = (n, d = 2) => n.toFixed(d);
const clamp01 = (n) => Math.max(0, Math.min(1, n));
const smoothstep = (a, b, x) => {
	const t = clamp01((x - a) / (b - a));
	return t * t * (3 - 2 * t);
};

// The visible part of an eye, as a clip path in the eye's own coordinates, for
// an eye of live size w x h - plus the lid's edge, drawn as a warm-white line so
// a lowered lid keeps the eye's outline instead of leaving it looking cut off.
//
// Lids are CLIPPED, not painted over. Painting a dark lid over a white eye
// leaves a faint ring where the antialiased edges don't quite cancel - visible on
// every blink. A clip removes the pixels instead, so nothing can leak.
//
// The upper lid comes down from the top; for a blink the lower edge rises to meet
// it on a straight line. There is no expressive lower lid: on a round eye any cut
// from below reads as a slice, not a smile. `outer` is which side this eye's
// outer corner is on (-1 left eye, +1 right): a sad face drops the OUTER
// corners (`sad`), a cross one drops the INNER corners (`mad`).
function lidShape(top, bottom, sad, mad, outer, w, h) {
	const xw = w / 2 + 40;
	const span = h + 24;
	const yT = -h / 2 - 12 + top * span;
	const yB = h / 2 + 12 - bottom * span;
	const yOuter = yT + sad * 0.36 * h - mad * 0.04 * h;
	const yInner = yT - sad * 0.04 * h + mad * 0.55 * h;
	// A quadratic stays inside its control points, so clamping all three to the
	// bottom edge guarantees a drooped lid can never fold under itself mid-blink.
	const yl = Math.min(outer < 0 ? yOuter : yInner, yB);
	const yr = Math.min(outer < 0 ? yInner : yOuter, yB);
	// The top edge bows upward like a real upper lid, flattening as the eye
	// closes so a blink still shuts along a straight line.
	// It relaxes as a corner drops, or the bow would lift the lid back over the
	// very droop that makes a face sad or cross.
	const cT = Math.min((yl + yr) / 2 - ARCH * (1 - top) * (1 - 0.6 * Math.max(sad, mad)), yB);
	const edge = `M ${fx(-xw, 1)} ${fx(yl, 1)} Q 0 ${fx(cT, 1)} ${fx(xw, 1)} ${fx(yr, 1)}`;
	// Fully shut, nothing of the eye shows - only the edge, as a closed-eye line.
	const clip = top + bottom >= 0.995 ? "M 0 0 Z" : `${edge} L ${fx(xw, 1)} ${fx(yB, 1)} L ${fx(-xw, 1)} ${fx(yB, 1)} Z`;
	return { clip, edge };
}

// The mouth's line: an "ω" when she smiles - two little bumps meeting in the
// middle, BellaBot's cat mouth - flattening to a line and turning into a frown
// below zero. `wave` bends it into the uneasy "~" of an error.
function mouthLine(c) {
	const s = Math.max(-1, Math.min(1, c.smile));
	const W = MOUTH.hw * c.mouthW;
	let y0 = 0;
	let ym = 0;
	let cq = 0;
	if (s >= 0) {
		y0 = -3 * s; // the corners tuck up a touch
		cq = 2 * s * MOUTH.dip; // each bump bottoms out at s * dip
	} else {
		y0 = -s * 16; // corners down
		ym = s * 5; // middle up
		cq = ym; // a smooth arc through the middle
	}
	const wv = clamp01(c.mouthWave) * 22;
	return `M ${fx(-W, 1)} ${fx(y0, 1)} Q ${fx(-W / 2, 1)} ${fx(cq - wv, 1)} 0 ${fx(ym, 1)} Q ${fx(W / 2, 1)} ${fx(cq + wv, 1)} ${fx(W, 1)} ${fx(y0, 1)}`;
}

// The talking mouth - one soft outline that takes the shape of each sound:
// `mouthOpen` drops the jaw, `mouthWide` spreads it wide ("ee") or gathers it
// round ("oo"). Shut, it is a small smiling line, so the pauses between words
// look like lips meeting rather than a mouth vanishing. A smile flattens the top
// edge into a grin.
function talkPath(c) {
	const o = clamp01(c.mouthOpen);
	const wd = clamp01(c.mouthWide);
	const s = Math.max(0, Math.min(1, c.smile));
	// Eased so even a small opening shows dark inside rather than a white blob.
	const oe = Math.pow(o, 0.65);
	const W = MOUTH.hw * c.mouthW * (0.48 + 0.6 * wd) * (1 - 0.15 * o * (1 - wd));
	// The upper lip lifts, most for round sounds - an "oo" is an oval, and cannot
	// smile at the same time, so the grin's flat top fades as the mouth rounds.
	const top = s * 7 * wd - oe * (6 + 18 * (1 - wd));
	const bot = 4 + oe * (40 + 8 * (1 - wd));
	// A cubic's midpoint sits at 3/4 of its control offset, hence the division.
	const t = top / 0.75;
	const b = bot / 0.75;
	const k = W * 0.9;
	return `M ${fx(-W, 1)} 0 C ${fx(-k, 1)} ${fx(t, 1)} ${fx(k, 1)} ${fx(t, 1)} ${fx(W, 1)} 0 C ${fx(k, 1)} ${fx(b, 1)} ${fx(-k, 1)} ${fx(b, 1)} ${fx(-W, 1)} 0 Z`;
}

// When no measured mouth shape is coming in - the teleop voice plays through an
// element the analyser never taps - she still says something that looks like
// words: vowel shapes in a natural mix, one per syllable.
const FAKE_SOUNDS = [
	{ open: 0.95, wide: 0.55 }, // aa
	{ open: 0.6, wide: 0.8 }, // eh
	{ open: 0.35, wide: 1 }, // ee
	{ open: 0.65, wide: 0.2 }, // oh
	{ open: 0.35, wide: 0 }, // oo
	{ open: 0, wide: 0.5 }, // m, b, p
	{ open: 0.08, wide: 0.75 }, // s, t
];

function paint(e, c, blink, speakGlow, dotPhase) {
	if (!e.face) return;

	const hx = c.headX + c.lookX * GAZE.headX;
	const hy = c.headY + c.lookY * GAZE.headY;
	e.face.setAttribute("transform", `translate(${fx(hx)} ${fx(hy)}) rotate(${fx(c.headTilt)} ${PIVOT.x} ${PIVOT.y})`);
	e.face.style.opacity = fx(clamp01(c.bright), 3);

	const pos = (v) => Math.max(0, v);
	const sum = pos(c.open) + pos(c.arcUp) + pos(c.arcSoft) + pos(c.arcDown) + pos(c.squeeze) || 1;
	const wOpen = pos(c.open) / sum;
	const wUp = pos(c.arcUp) / sum;
	const wSoft = pos(c.arcSoft) / sum;
	const wDown = pos(c.arcDown) / sum;
	const wSqueeze = pos(c.squeeze) / sum;

	for (const side of ["L", "R"]) {
		const sg = side === "L" ? -1 : 1;
		const wink = side === "R" ? clamp01(c.winkR) : 0;
		// How shut this eye is by mode (the closed lines) or by the wink.
		const closure = 1 - wOpen * (1 - wink);

		const persp = 1 + GAZE.persp * c.lookX * sg;
		const asym = sg > 0 ? 1 + c.eyeAsym : 1 - 0.6 * c.eyeAsym;
		const k = c.eyeScale * persp * asym;
		const w = EYE.w * k * (1 - GAZE.narrow * Math.abs(c.lookX));
		const h = EYE.h * k * c.eyeTall * (1 - GAZE.flat * Math.abs(c.lookY));
		const cx = side === "L" ? EYE.left : EYE.right;
		const ex = cx + c.lookX * GAZE.x;
		const ey = EYE.y + c.lookY * GAZE.y;
		e[`eye${side}`].setAttribute("transform", `translate(${fx(ex)} ${fx(ey)})`);

		for (const el of [e[`white${side}`], e[`shape${side}`]]) {
			el.setAttribute("rx", fx(w / 2, 1));
			el.setAttribute("ry", fx(h / 2, 1));
		}
		// The pupil looks around inside the eye; the eye's own outline clips it.
		const px = c.lookX * IRIS.travelX * k;
		const py = c.lookY * IRIS.travelY * k;
		e[`iris${side}`].setAttribute("transform", `translate(${fx(px, 1)} ${fx(py, 1)}) scale(${fx(k, 3)})`);
		e[`shine${side}`].setAttribute(
			"transform",
			`translate(${fx(-c.lookX * IRIS.travelX * GAZE.shineLag, 1)} ${fx(-c.lookY * IRIS.travelY * GAZE.shineLag, 1)})`,
		);

		const squeeze = Math.min(1, closure / CLOSE.shutAt);
		const top = Math.min(1, c.lidTop + (side === "R" ? c.squintR : 0) + 0.62 * (blink + squeeze));
		const bottom = Math.min(1, 0.42 * (blink + squeeze));
		const lid = lidShape(top, bottom, c.sadLids, c.madLids, sg, w, h);
		e[`ap${side}`].setAttribute("d", lid.clip);
		e[`lid${side}`].setAttribute("d", lid.edge);
		e[`lid${side}`].style.strokeWidth = fx(LID_LINE * k, 1);

		// The closed lines share out the closure between them.
		const env = smoothstep(CLOSE.arcFrom, 1, closure);
		const share = (q) => (closure > 0.001 ? (env * q) / closure : 0);
		// The lid's edge hands over to the closed line as it fades in.
		e[`lid${side}`].style.opacity = fx(1 - env, 3);
		e[`arcs${side}`].setAttribute("transform", `scale(${fx(c.eyeScale * asym, 3)})`);
		e[`arcUp${side}`].style.opacity = fx(share(wUp * (1 - wink)), 3);
		e[`arcSoft${side}`].style.opacity = fx(share(wSoft * (1 - wink)), 3);
		e[`arcDown${side}`].style.opacity = fx(share(wDown * (1 - wink)), 3);
		e[`squeeze${side}`].style.opacity = fx(share(wSqueeze * (1 - wink)), 3);
		if (side === "R") e.wink.style.opacity = fx(share(wink), 3);
		e[`tears${side}`].style.opacity = fx(clamp01(c.tears), 3);

		// Brows ride up when the eyes grow, so a surprised face never pushes the
		// eye into its own brow. The left brow's inner end is its right end, so
		// "inner end up" is an anticlockwise turn on the left and clockwise on the
		// right.
		const bx = cx - sg * BROW.inset + c.lookX * GAZE.x * 0.8;
		const by = BROW.y + c.browY - (h - EYE.h) / 2 + c.lookY * GAZE.y * 0.6 + (side === "R" ? c.browRY : 0);
		e[`brow${side}`].setAttribute("transform", `translate(${fx(bx)} ${fx(by)}) rotate(${fx(sg * c.browAng)})`);

		// Whiskers turn on their inner ends: up when she is pleased, down when
		// sorry or sleepy. The right set is the left one mirrored.
		const wx = side === "L" ? WHISKER.x : 1600 - WHISKER.x;
		e[`whisk${side}`].setAttribute(
			"transform",
			`translate(${fx(wx + c.lookX * GAZE.x * 0.4)} ${fx(WHISKER.y + c.lookY * GAZE.y * 0.3)}) scale(${-sg} 1) rotate(${fx(c.whisk * 9)})`,
		);
	}

	const mx = MOUTH.x + c.lookX * GAZE.x * GAZE.mouthX;
	const my = MOUTH.y + c.lookY * GAZE.y * GAZE.mouthY;
	e.mouth.setAttribute("transform", `translate(${fx(mx)} ${fx(my)}) rotate(${fx(c.mouthTilt * 12)})`);
	const dots = clamp01(c.dots);
	const talk = clamp01(c.talk);
	e.mouthLine.style.opacity = fx((1 - talk) * (1 - dots), 3);
	if (talk < 0.999) e.mouthLine.setAttribute("d", mouthLine(c));
	e.mouthTalk.style.opacity = fx(talk * (1 - dots), 3);
	if (talk > 0.001) e.mouthTalk.setAttribute("d", talkPath(c));
	e.dots.style.opacity = fx(dots, 3);
	e.huff.style.opacity = fx(clamp01(c.huff), 3);
	if (dots > 0.001) {
		for (let i = 0; i < 3; i++) {
			e[`dot${i}`].setAttribute("cy", fx(-10 * clamp01((dotPhase[i] - 0.35) / 0.65), 1));
			e[`dot${i}`].style.opacity = fx(dotPhase[i], 3);
		}
	}

	// The speaking glow swells while she talks. Opacity only - the glow itself is
	// painted once, so this stays a cheap compositor change every frame.
	if (e.aura) e.aura.style.setProperty("--glow", fx(speakGlow, 3));
}

const rnd = (a, b) => a + Math.random() * (b - a);

export default function BellaFace({ store = faceStore }) {
	const snap = useSyncExternalStore(store.subscribe, store.getSnapshot);
	const els = useRef({});
	const ref = (name) => (el) => {
		els.current[name] = el;
	};

	useEffect(() => {
		const cur = { ...DEFAULTS };
		let raf = 0;
		let last = performance.now();
		const t0 = last;
		let prevState = store.getSnapshot().state;
		let prevEmotion = store.getSnapshot().emotion;

		// Aliveness schedules.
		let blinkAt = last + rnd(1500, 4000);
		let blinkStart = -1;
		let blinkDur = 170;
		let secondBlink = false;
		let glanceAt = last;
		let glanceX = 0;
		let glanceY = 0;
		let nodStart = -1;
		let shakeStart = -1;
		let hopStart = -1;
		let contentAt = last + rnd(18000, 40000);
		let contentStart = -1;
		let amp = 0;
		let sound = FAKE_SOUNDS[0];
		let soundAt = last;
		// A much slower copy of the voice level, for the edge glow only. See below.
		let glow = 0;
		const dotPhase = [0.35, 0.35, 0.35];

		const loop = () => {
			const now = store.frame();
			// The springs are exact exponential decay, stable at any step size, so a
			// long frame just lands closer to the target - after a stall the face
			// catches up rather than crawls. The cap only guards against a stale
			// timestamp after the tab was hidden.
			const dt = Math.min(0.25, Math.max(0.001, (now - last) / 1000));
			last = now;
			const t = (now - t0) / 1000;
			const { state, emotion } = store.getSnapshot();
			const tgt = targetFor(state, emotion);

			// "Got it." The instant a guest finishes and she starts thinking, a quick
			// nod - so the gap before her answer never looks like she missed it.
			if (state !== prevState) {
				if (state === "thinking" && (prevState === "hearing" || prevState === "listening")) nodStart = now;
				prevState = state;
			}
			if (emotion !== prevEmotion) {
				if (emotion === "excited" || emotion === "delighted" || emotion === "surprised") hopStart = now;
				// Serving: a little bow as she presents it. Frustrated: a "hmph" shake.
				if (emotion === "smile") nodStart = now;
				if (emotion === "frustrated") shakeStart = now;
				prevEmotion = emotion;
			}

			// --- Gaze. Where she looks says as much as the eye shape. -----------
			if (now >= glanceAt) {
				if (state === "idle" || state === "drowsy") {
					// Looking around the room and coming back to centre often.
					const home = Math.random() < 0.4;
					glanceX = home ? 0 : rnd(-0.6, 0.6);
					glanceY = home ? 0 : rnd(-0.3, 0.3);
					glanceAt = now + rnd(1200, 3500);
				} else if (state === "listening" || state === "hearing") {
					// Locked on the guest, with only the tiny movements real eyes never
					// stop making. Dead-still eyes read as a photograph.
					glanceX = rnd(-0.06, 0.06);
					glanceY = rnd(-0.05, 0.05);
					glanceAt = now + rnd(600, 1500);
				} else if (state === "speaking") {
					glanceX = rnd(-0.2, 0.2);
					glanceY = rnd(-0.12, 0.08);
					glanceAt = now + rnd(900, 2200);
				} else if (state === "thinking") {
					glanceX = rnd(-0.1, 0.1);
					glanceY = rnd(-0.06, 0.06);
					glanceAt = now + rnd(700, 1600);
				} else {
					glanceX = 0;
					glanceY = 0;
					glanceAt = now + 1000;
				}
			}
			let lookX = tgt.lookX + glanceX;
			const lookY = tgt.lookY + glanceY;
			if (state === "working") lookX = Math.sin(t * 1.9) * 0.5; // reading the menu

			// --- The face moves as a whole, like a head. -------------------------
			let headY = tgt.headY;
			let headTilt = tgt.headTilt;
			let eyeTall = tgt.eyeTall;
			let browY = tgt.browY;
			let arcUp = tgt.arcUp;
			let open = tgt.open;
			const asleep = state === "asleep";
			headY += Math.sin((t * 2 * Math.PI) / (asleep ? 5.2 : 4.2)) * (asleep ? 8 : 3);
			if (state === "hearing") headY += Math.sin(t * 2 * Math.PI * 1.6) * 5;
			// Small hops for a greeting, a compliment, or hearing her name - once,
			// not a bounce that goes on for the whole emotion.
			if (hopStart >= 0) {
				const q = (now - hopStart) / 340;
				const hops = emotion === "surprised" ? 1 : 2;
				if (q >= hops) hopStart = -1;
				else headY -= Math.abs(Math.sin(q * Math.PI)) * 16;
			}
			if (emotion === "love") headTilt += Math.sin(t * 1.7) * 3;
			if (nodStart >= 0) {
				const q = (now - nodStart) / 420;
				if (q >= 1) nodStart = -1;
				else {
					headY += Math.sin(q * Math.PI) * 18;
					// ...and the eyes squish a little as the head dips: "mm, got it".
					eyeTall -= Math.sin(q * Math.PI) * 0.1;
					browY += Math.sin(q * Math.PI) * 8;
				}
			}

			// Now and then, when nobody needs her, a small contented "^ ^" - the kind
			// of thing that makes people smile back at a robot.
			if (contentStart < 0 && now >= contentAt && state === "idle" && !emotion) contentStart = now;
			if (contentStart >= 0) {
				const q = (now - contentStart) / 1300;
				if (q >= 1 || state !== "idle" || emotion) {
					contentStart = -1;
					contentAt = now + rnd(20000, 45000);
				} else {
					const k = Math.sin(Math.min(1, q * 1.4) * Math.PI);
					arcUp = Math.max(arcUp, k);
					open = open * (1 - k);
				}
			}

			// --- Mouth follows her actual voice. --------------------------------
			// Real amplitude when the analyser is feeding it. When it is not - the
			// teleop voice plays through its own <audio> element the analyser never
			// taps, and the tap can fail to attach - fall back to a syllable-like
			// envelope, so she never speaks with a frozen mouth.
			const speaking = state === "speaking";
			const measured = now - store.live.ampAt < 250;
			const synthetic = 0.18 + 0.5 * Math.abs(Math.sin(t * 12.5)) * Math.abs(Math.sin(t * 4.3 + 0.7));
			const raw = speaking ? (measured ? store.live.amplitude : synthetic) : 0;
			amp += (raw - amp) * 0.45;
			// The mouth shape: measured from her voice when the analyser sends one,
			// otherwise a plausible vowel per syllable, opened as far as she is loud.
			let mouthOpen = tgt.mouthOpen;
			let mouthWide = tgt.mouthWide;
			if (speaking) {
				const v = measured && store.live.viseme;
				if (v) {
					mouthOpen = Math.max(mouthOpen, v.open);
					mouthWide = v.wide;
				} else {
					if (now >= soundAt) {
						let next = sound;
						while (next === sound) next = FAKE_SOUNDS[Math.floor(Math.random() * FAKE_SOUNDS.length)];
						sound = next;
						soundAt = now + rnd(90, 170);
					}
					mouthOpen = Math.max(mouthOpen, sound.open * Math.min(1, amp * 1.6));
					mouthWide = sound.wide;
				}
			}
			// Talking moves the whole face a little, whiskers included.
			if (speaking) headY -= amp * 6;
			const whisk = tgt.whisk + (speaking ? amp * 0.35 : 0);

			// The dots brighten and lift one after another while she looks it up.
			for (let i = 0; i < 3; i++) dotPhase[i] = 0.35 + 0.65 * Math.max(0, Math.sin((t * 2 * Math.PI) / 1.2 - i * 0.9));

			const target = { ...tgt, lookX, lookY, headY, headTilt, eyeTall, browY, arcUp, open, mouthOpen, mouthWide, whisk };
			for (const k in cur) {
				const s = STIFFNESS[k] || 8;
				cur[k] += (target[k] - cur[k]) * (1 - Math.exp(-dt * s));
			}

			// --- Blinks: added on top, not sprung - a blink has to be crisp. ------
			let blink = 0;
			if (blinkStart < 0 && now >= blinkAt && cur.open > 0.5 && !asleep) {
				blinkStart = now;
				blinkDur = state === "drowsy" ? 480 : 170;
			}
			if (blinkStart >= 0) {
				const q = (now - blinkStart) / blinkDur;
				if (q >= 1) {
					blinkStart = -1;
					if (!secondBlink && Math.random() < 0.14) {
						secondBlink = true; // the occasional double blink
						blinkAt = now + 90;
					} else {
						secondBlink = false;
						const [a, b] =
							state === "listening" || state === "hearing" ? [3500, 7500] : state === "drowsy" ? [2500, 4500] : [2200, 5500];
						blinkAt = now + rnd(a, b);
					}
				} else {
					// Fast close, slower open - how eyelids actually move.
					blink = q < 0.4 ? q / 0.4 : 1 - (q - 0.4) / 0.6;
				}
			}

			// Fast, small movements go on top of the springs, which would smooth them
			// away: sobs, the "hmph" head shake, a quivering lip, twitching whiskers.
			let view = cur;
			if (emotion === "cry" || emotion === "frustrated" || shakeStart >= 0) {
				view = { ...cur };
				if (emotion === "cry") {
					view.headY += Math.abs(Math.sin(t * Math.PI * 2 * 2.2)) * 7;
					view.mouthWave = clamp01(cur.mouthWave + 0.25 + 0.2 * Math.sin(t * 15));
				}
				if (emotion === "frustrated") view.whisk += Math.sin(t * 22) * 0.2;
				if (shakeStart >= 0) {
					const q = (now - shakeStart) / 700;
					if (q >= 1) shakeStart = -1;
					else view.headX += Math.sin(q * Math.PI * 6) * 16 * (1 - q);
				}
			}

			// The glow must NOT follow the voice syllable by syllable - a full-screen
			// edge flashing at speech rate breaks WCAG 2.3.1. See aura.js, and
			// aura.test.js, which holds it under three flashes a second.
			glow = stepGlow(glow, speaking ? amp * 1.3 : 0, dt);
			paint(els.current, view, blink, speaking ? auraOpacity(glow) : 0, dotPhase);
			raf = requestAnimationFrame(loop);
		};

		raf = requestAnimationFrame(loop);
		return () => cancelAnimationFrame(raf);
	}, [store]);

	const aura = auraFor(snap.state);

	const eye = (side) => (
		<g ref={ref(`eye${side}`)} transform={`translate(${side === "L" ? EYE.left : EYE.right} ${EYE.y})`}>
			<clipPath id={`bellaAp${side}`}>
				<path ref={ref(`ap${side}`)} d={lidShape(0, 0, 0, 0, side === "L" ? -1 : 1, EYE.w, EYE.h).clip} />
			</clipPath>
			<clipPath id={`bellaShape${side}`}>
				<ellipse ref={ref(`shape${side}`)} rx={EYE.w / 2} ry={EYE.h / 2} />
			</clipPath>
			<g clipPath={`url(#bellaAp${side})`}>
				<g clipPath={`url(#bellaShape${side})`}>
					<ellipse ref={ref(`white${side}`)} rx={EYE.w / 2} ry={EYE.h / 2} className="bella-eye" />
					<g ref={ref(`iris${side}`)}>
						<ellipse rx={IRIS.rx} ry={IRIS.ry} className="bella-bead" />
						<g ref={ref(`shine${side}`)} className="bella-shine">
							<ellipse cx="-48" cy="-58" rx="34" ry="40" />
							<circle cx="46" cy="44" r="14" opacity="0.9" />
						</g>
					</g>
				</g>
			</g>
			{/* Only inside the eye's outline: at rest the edge arches above the eye
			    and is not seen at all. */}
			<path ref={ref(`lid${side}`)} clipPath={`url(#bellaShape${side})`} className="bella-lid" />
			<g ref={ref(`arcs${side}`)}>
				<path ref={ref(`arcUp${side}`)} d={ARC_UP} className="bella-line" style={{ opacity: 0 }} />
				<path ref={ref(`arcSoft${side}`)} d={ARC_SOFT} className="bella-line" style={{ opacity: 0 }} />
				<path ref={ref(`arcDown${side}`)} d={ARC_DOWN} className="bella-line" style={{ opacity: 0 }} />
				<path ref={ref(`squeeze${side}`)} d={side === "L" ? SQUEEZE_L : SQUEEZE_R} className="bella-line" style={{ opacity: 0 }} />
				{side === "R" && <path ref={ref("wink")} d={WINK} className="bella-line" style={{ opacity: 0 }} />}
			</g>
			{/* Tears fall from the outer corners. Each drop is placed by its wrapper
			    and animated by CSS on the group inside - an animated transform would
			    otherwise replace the one that places it. */}
			<g ref={ref(`tears${side}`)} style={{ opacity: 0 }}>
				{[0, 1, 2, 3].map((i) => (
					<g key={i} transform={`translate(${side === "L" ? -66 : 66} 64)`}>
						<g className="bella-tear" style={{ animationDelay: `${(i * 0.29).toFixed(2)}s` }}>
							<path d={TEAR} />
						</g>
					</g>
				))}
			</g>
		</g>
	);

	// A little arched bean, like BellaBot's - one thick round-capped stroke.
	const brow = (side) => (
		<g ref={ref(`brow${side}`)}>
			<path d="M -50 10 Q 0 -16 50 10" className="bella-brow" />
		</g>
	);

	// Two whiskers fanning out from the cheek, drawn for the left side and
	// mirrored for the right.
	const whiskers = (side) => (
		<g ref={ref(`whisk${side}`)}>
			<path d="M 0 -14 L -72 -32" className="bella-whisker" />
			<path d="M 0 14 L -74 20" className="bella-whisker" />
		</g>
	);

	return (
		<>
			<svg
				className={`bella bella--${snap.state}${snap.emotion ? ` bella--${snap.emotion}` : ""}`}
				viewBox="0 0 1600 1000"
				preserveAspectRatio="xMidYMid meet"
				data-state={snap.state}
				data-emotion={snap.emotion || ""}
				aria-hidden="true"
			>
				<g ref={ref("face")}>
					{whiskers("L")}
					{whiskers("R")}
					{brow("L")}
					{brow("R")}
					{eye("L")}
					{eye("R")}
					<g ref={ref("mouth")} transform={`translate(${MOUTH.x} ${MOUTH.y})`}>
						<path ref={ref("mouthLine")} className="bella-mouth" />
						<path ref={ref("mouthTalk")} className="bella-mouth-talk" style={{ opacity: 0 }} />
						<g ref={ref("dots")} style={{ opacity: 0 }}>
							{[-DOTS_DX, 0, DOTS_DX].map((x, i) => (
								<circle key={x} ref={ref(`dot${i}`)} cx={x} cy="0" r="17" className="bella-dot" />
							))}
						</g>
					</g>
					{/* Frustration: a cross mark above her brow and a puff of steam. */}
					<g ref={ref("huff")} style={{ opacity: 0 }}>
						<g transform="translate(1290 172)">
							<g className="bella-cross">
								<path d={CROSS} />
							</g>
						</g>
						<g transform="translate(1318 512)">
							<g className="bella-puff">
								<circle cx="0" cy="0" r="22" />
								<circle cx="30" cy="-14" r="28" />
								<circle cx="60" cy="2" r="20" />
							</g>
						</g>
					</g>
				</g>
			</svg>
			{/* Status on the screen edge - BellaBot shows its state on its lit ears.
			    Each state differs in colour AND rhythm, so they still read for
			    colour-blind guests. */}
			<div ref={ref("aura")} className={`bella-aura${aura ? ` bella-aura--${aura}` : ""}`} aria-hidden="true" />
		</>
	);
}
